import type { Entity } from '@tunasong/schemas'

/**
 * The right rail shows these tools, in this order. Everything else sits behind the
 * "More panels" menu. The tool names (and so persisted `activeTool` values) are unchanged;
 * only the user-facing labels differ (see `getToolLabel`).
 *
 * Plan: docs/superpowers/plans/2026-10-10-song-main-declutter-plan.md (PR 6).
 */
const primaryToolNames = ['Chat', 'My Notes', 'Activities', 'Suggestions', 'Settings'] as const

/** Tools the Assist panel already shows, so More does not list them a second time */
const inAssistToolNames = ['Context']

const toolLabels: Record<string, string> = {
  'My Notes': 'Notes',
  Activities: 'Activity',
  Suggestions: 'Assist',
}

/** One-line panel descriptions for the rail tools. Other tools keep their own `description`. */
const toolDescriptions: Record<string, string> = {
  Chat: 'Comments on this song from you and your band.',
  'My Notes': 'Only you can see these. They also show in Perform.',
  Activities: 'Recent changes to this song.',
  Suggestions: 'Chord, note and scale suggestions for where your cursor is.',
  Settings: 'Name, key, meter and tempo for this song.',
}

/** What a rail count counts, singular and plural */
const countNouns: Record<string, [string, string]> = {
  Chat: ['open comment', 'open comments'],
  'My Notes': ['note', 'notes'],
}

interface ToolLike {
  name: string
  disabled: (entity: Entity) => boolean
}

/** User-facing label for a sidebar tool */
export const getToolLabel = (name: string) => toolLabels[name] ?? name

/** Panel description that replaces the tool's own, if any */
export const getToolDescription = (name: string): string | undefined => toolDescriptions[name]

/**
 * The rail button's accessible name. A visible count badge is spoken too ("Chat, 2 open comments"),
 * and Assist says what it holds.
 */
export const getRailLabel = (name: string, count: number) => {
  if (name === 'Suggestions') {
    return 'Assist: suggestions and context'
  }
  const label = getToolLabel(name)
  const nouns = countNouns[name]
  if (!nouns || count <= 0) {
    return label
  }
  return `${label}, ${count} ${count === 1 ? nouns[0] : nouns[1]}`
}

export const isPrimaryTool = (name: string) => (primaryToolNames as readonly string[]).includes(name)

/**
 * Split the tool list into the rail (`primary`, fixed order, disabled tools kept so the rail does
 * not shift between entity types) and the More menu (`more`, source order, disabled tools dropped).
 */
export const partitionSidebarTools = <T extends ToolLike>(tools: T[], entity: Entity | undefined) => {
  const primary = primaryToolNames.map(name => tools.find(t => t.name === name)).filter((t): t is T => t !== undefined)
  const more = entity
    ? tools.filter(t => !isPrimaryTool(t.name) && !inAssistToolNames.includes(t.name) && !t.disabled(entity))
    : []
  return { primary, more }
}
